import { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * EntityStatusPicker - Optional open/closed status picker for sighting forms
 *
 * Renders two pill toggle buttons ("Open" / "Closed"). Tapping the active
 * selection deselects it (sets value back to null).
 *
 * @param {string|null} props.value - Current status: 'open' | 'closed' | null
 * @param {Function} props.onChange - Called with new status string or null
 *
 * @idempotent - Same props always produce same output
 * @atomic - Single responsibility: entity status selection
 */
const EntityStatusPicker = memo(function EntityStatusPicker({ value, onChange }) {
  const { t } = useTranslation()

  const handleToggle = useCallback((id) => {
    onChange(value === id ? null : id)
  }, [value, onChange])

  const handleClear = useCallback(() => {
    onChange(null)
  }, [onChange])

  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {t('sightings.status.prompt', 'Is it open?')}
        <span className="ml-1 text-xs text-gray-400">
          ({t('sightings.status.optional', 'optional')})
        </span>
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => handleToggle('open')}
          onTouchEnd={(e) => { e.preventDefault(); handleToggle('open') }}
          className={`flex-1 py-1.5 px-3 rounded-full text-xs font-medium border transition-colors ${
            value === 'open'
              ? 'bg-green-500 text-white border-green-500'
              : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
          }`}
        >
          {t('sightings.status.open', 'Open')}
        </button>
        <button
          type="button"
          onClick={() => handleToggle('closed')}
          onTouchEnd={(e) => { e.preventDefault(); handleToggle('closed') }}
          className={`flex-1 py-1.5 px-3 rounded-full text-xs font-medium border transition-colors ${
            value === 'closed'
              ? 'bg-red-500 text-white border-red-500'
              : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
          }`}
        >
          {t('sightings.status.closed', 'Closed')}
        </button>
        {value && (
          <button
            type="button"
            onClick={handleClear}
            onTouchEnd={(e) => { e.preventDefault(); handleClear() }}
            className="py-1.5 px-3 rounded-full text-xs font-medium border border-gray-200 text-gray-400 hover:border-gray-300 transition-colors"
          >
            {t('sightings.status.clear', 'Clear')}
          </button>
        )}
      </div>
    </div>
  )
})

export default EntityStatusPicker
